import {
  Box,
  Chip,
  TextField,
} from "@mui/material";
import React, { useState, useMemo } from "react";
import ReusableDataTable from "../../Component/DataTable/ReusableDataTablePrime";
import { FilterMatchMode } from "primereact/api";
import { useDispatch, useSelector } from "react-redux";
import { setLoading } from "../../store/CommonSlice";
import { CustomMessage } from "../../const/Spinner";
import { enqueueSnackbar } from "notistack";
import ReusablePrimeButton from "../../Component/Buttons/ReusablePrimeButton";
import AddIcon from "@mui/icons-material/Add";
import { LocalStoreMaster } from "../../types/assetTypes";
import { getLocalStoreColumns } from "../../const/AssertData";
import {
  getLocalStoreById,
  getLocalStoreMasterDataList,
  saveLocalStore
} from "../../api/Services/masterServie";
import ReusablePrimeReactDialog from "../../Component/Dialog/ReusablePrimeReactDialog";
import { RootState } from "../../store/Reduxstore";

interface LocalStoreProps {
  setPathname: (path: string) => void;
}

const LocalStore: React.FC = () => {
  const dispatch = useDispatch();
  const [data, setData] = useState<LocalStoreMaster[]>([]);
  const [pagination, setPagination] = useState({
    page: 1,
    rows: 13,
    totalRecords: 0,
  });
  const [openDialog, setOpenDialog] = useState(false);
  const [localStoreData, setLocalStoreData] =
    useState<LocalStoreMaster>({
      localStoreId: 0,
      localStore: "",
      localStores: []
    });

  // Memoized column definition
  const columns = useMemo(() => getLocalStoreColumns(), []);

  if (columns.filter((val) => val.field === "action").length === 0) {
    columns.push({
      field: "action",
      header: "Action",
      sortable: false,
      filter: false,
      filterField: "action",
      filterPlaceholder: "Search",
      style: { minWidth: "12rem" },
      body: (rowData: any) => (
        <ReusablePrimeButton
          icon="pi pi-pencil"
          className="p-button-rounded p-button-outlined"
          onClick={() => handleEditFamilyAsset(rowData.localStoreId)}
        />
      ),
    });
  }

  const [filterData, setFilterData] = useState<Record<string, any>>(() =>
    (columns ?? []).reduce((acc, col) => {
      if (col.field) {
        acc[col.field] = { value: "", matchMode: "startsWith" }; // Ensure correct structure
      }
      return acc;
    }, {} as Record<string, { value: string; matchMode: string }>),
  );

  const handleEditFamilyAsset = async (localStoreId: number) => {
    dispatch(setLoading(true));
    try {
      const response = await getLocalStoreById(localStoreId);
      let { data, status } = response;
      if (status.code === 200 && data) {
        setLocalStoreData(data);
        setOpenDialog(true);
      } else {
        CustomMessage(status.message, "error", enqueueSnackbar);
      }
    } catch (error: any) {
      CustomMessage(error.message, "error", enqueueSnackbar);
    } finally {
      dispatch(setLoading(false));
    }
  };

  // Generate filters dynamically
  const filters = useMemo(() => {
    return columns.reduce(
      (acc, col) => {
        if (col.field) {
          acc[col.field] = {
            value: null,
            matchMode: FilterMatchMode.STARTS_WITH,
          };
        }
        return acc;
      },
      {} as Record<string, { value: any; matchMode: FilterMatchMode }>
    );
  }, [columns]);

  const onFetchData = async (page: number, rows: number, filter?: Record<string, any>) => {
    dispatch(setLoading(true));
    try {
      const { data, status, pagination } = await getLocalStoreMasterDataList(page, rows, filter ?? filterData);
      if (status.code === 200 && data?.length > 0) {
        setData(data);
        setPagination({
          page: pagination.currentPage,
          rows: pagination.limit,
          totalRecords: pagination.totalCount,
        });
      } else {
        setData([]);
        CustomMessage(status.message, "error", enqueueSnackbar);
      }
    } catch (error: any) {
      CustomMessage(error?.message, "error", enqueueSnackbar);
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value, name } = event.target;
    setLocalStoreData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleClear = () => {
    setOpenDialog(false);
    setLocalStoreData({
      localStoreId: 0,
      localStore: "",
      localStores: []

    });
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event?.preventDefault();
    if (localStoreData.localStore === "") {
      CustomMessage("Add the local store", "error", enqueueSnackbar);
      return;
    }
    dispatch(setLoading(true));
    try {
      const response = await saveLocalStore(localStoreData);
      let { status } = response;
      if (status.code === 200 || status.code === 201) {
        CustomMessage(status.message, "success", enqueueSnackbar);
        handleClear();
        onFetchData(PAGESTART, PAGESEND, filterData);
      } else {
        CustomMessage(status.message, "error", enqueueSnackbar);
      }
    } catch (error: any) {
      CustomMessage(error?.message, "error", enqueueSnackbar);
    } finally {
      dispatch(setLoading(false));
    }
  };

  const userDetails = useSelector((state: RootState) => state.auth)

  return (
    <>
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
        }}
      >
        {userDetails.role.some(
          r => r.roleName === "Admin" || r.roleName === "Logitics List Write"
        ) && (
            <Chip
              className="btn-black-outlined mb-3"
              label="New"
              color="warning"
              onClick={() => setOpenDialog(true)}
              icon={<AddIcon />}
              variant="outlined"
            />
          )}
      </div>
      <Box
        sx={{
          backgroundColor: "#f9f9f9",
          borderRadius: 3,
          boxShadow: 3,
          padding: 2,
        }}
      >
        <ReusableDataTable
          data={data}
          columns={columns}
          onFetchData={onFetchData}
          emptyMessage="No data found."
          paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
          selectionMode="multiple"
          rowsPerPageOptions={[5, 10, 25, 50]}
          pagination={pagination}
          setPagination={setPagination}
          filters={filterData}
          setFilters={setFilterData}
          onRowDoubleClick={(rowData) => {
            console.log('Double-click row:', rowData);
            handleEditFamilyAsset(rowData.localStoreId);
          }}
        />
      </Box>
      <ReusablePrimeReactDialog
        header="Add Local Store"
        visible={openDialog}
        onHide={handleClear}
      >
        <form>
          <h6>Local Store Name</h6>
          <TextField
            label=""
            fullWidth
            variant="outlined"
            value={localStoreData.localStore}
            name="localStore"
            onChange={handleChange}
            sx={{ mt: 0 }}
            required
          />


          <div style={{
            display: 'flex',
            justifyContent: 'flex-end',
            marginTop: "10px",
            gap: 3
          }}>
            <ReusablePrimeButton
              onClick={handleClear}
              severity="contrast"
              title="Cancel"

            >
              Cancel
            </ReusablePrimeButton>
            <ReusablePrimeButton severity="info" onClick={handleSubmit} allowedRoles={["Admin", "Logistics List Write"]}>
              Submit
            </ReusablePrimeButton>
          </div>
        </form>
      </ReusablePrimeReactDialog>
    </>
  );
};

export default LocalStore;
